/**
 * Project Settings Dialog — opened via Settings > Project...
 * Matches reference: assets/screenshots/Project-Settings.png
 *
 * Zone presets are sourced from zoneStore (shared with Transform tab / Program monitor).
 */

import { zoneStore, ZONE_PRESETS, renderZoneDiagram } from '../stores/zoneStore';
import { eventBus } from '../engine/eventBus';

// ── Dialog State ────────────────────────────────────────────

let overlay: HTMLElement | null = null;
let currentPresetIndex = 0;

// ── Public API ──────────────────────────────────────────────

export function openProjectSettings(): void {
  if (overlay) return;

  // Open dialog with the store's current preset pre-selected
  currentPresetIndex = zoneStore.getState().activePresetIndex;

  overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.innerHTML = `
    <div class="modal-dialog ps-dialog">
      <div class="modal-header">
        <img src="/assets/icons/app-icon-sm.png" alt="" class="ps-icon" onerror="this.style.display='none'">
        <span class="modal-title">Project Settings</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body ps-body">

        <!-- Display Preset -->
        <div class="ps-row ps-display-preset">
          <label class="ps-label">Display Preset:</label>
          <select class="ps-select ps-display-select">
            <option>Video Board Layouts</option>
          </select>
          <button class="ps-browse-btn" title="Browse presets">&#x1F4C2;</button>
        </div>

        <hr class="ps-divider">

        <!-- Video Format -->
        <h3 class="ps-section-title">Video Format</h3>

        <div class="ps-form">
          <div class="ps-form-row">
            <label class="ps-form-label">Video Preset:</label>
            <select class="ps-select ps-video-preset">
              <option>HD 720 &#x2022; 1280x720 &#x2022; 30 fps</option>
              <option>HD 1080 &#x2022; 1920x1080 &#x2022; 30 fps</option>
              <option>HD 1080 &#x2022; 1920x1080 &#x2022; 60 fps</option>
              <option>4K UHD &#x2022; 3840x2160 &#x2022; 30 fps</option>
            </select>
          </div>

          <div class="ps-form-row">
            <label class="ps-form-label">Width:</label>
            <input type="number" class="ps-input ps-width" value="1280">
            <label class="ps-form-label-mid">Height:</label>
            <input type="number" class="ps-input ps-height" value="720">
          </div>

          <div class="ps-form-row">
            <label class="ps-form-label">Frame Rate (fps):</label>
            <input type="number" class="ps-input ps-fps" value="30">
          </div>

          <div class="ps-form-row">
            <label class="ps-form-label">Interlaced:</label>
            <input type="checkbox" class="ps-checkbox ps-interlaced">
          </div>
        </div>

        <hr class="ps-divider">

        <!-- Zone Settings -->
        <h3 class="ps-section-title">Zone Settings</h3>

        <div class="ps-zone-diagram" id="ps-zone-diagram">
          ${renderZoneDiagram(ZONE_PRESETS[currentPresetIndex])}
        </div>

        <div class="ps-row ps-layout-preset">
          <label class="ps-label">Layout Preset:</label>
          <select class="ps-select ps-layout-select">
            ${ZONE_PRESETS.map((p, i) => `<option value="${i}"${i === currentPresetIndex ? ' selected' : ''}>${p.name}</option>`).join('')}
          </select>
          <button class="ps-browse-btn" title="Browse layouts">&#x1F4C2;</button>
        </div>

      </div>
      <div class="ps-footer">
        <button class="ps-btn ps-cancel">Cancel</button>
        <button class="ps-btn ps-save">Save Project Settings</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  eventBus.emit('ui:dialogOpened', { dialogId: 'project-settings' });

  // Populate video format fields from store
  const vf = zoneStore.getState().videoFormat;
  overlay.querySelector<HTMLInputElement>('.ps-width')!.value = String(vf.width);
  overlay.querySelector<HTMLInputElement>('.ps-height')!.value = String(vf.height);
  overlay.querySelector<HTMLInputElement>('.ps-fps')!.value = String(vf.fps);
  overlay.querySelector<HTMLInputElement>('.ps-interlaced')!.checked = vf.interlaced;

  // Select matching video preset dropdown option
  const presetSelect = overlay.querySelector<HTMLSelectElement>('.ps-video-preset')!;
  const presetLabel = `${vf.width}x${vf.height}`;
  for (let i = 0; i < presetSelect.options.length; i++) {
    if (presetSelect.options[i].text.includes(presetLabel)) {
      presetSelect.selectedIndex = i;
      break;
    }
  }

  attachHandlers();
}

// ── Event Handlers ──────────────────────────────────────────

function attachHandlers(): void {
  if (!overlay) return;

  // Close button
  overlay.querySelector('.modal-close')?.addEventListener('click', close);

  // Overlay background click
  overlay.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) close();
  });

  // Cancel button
  overlay.querySelector('.ps-cancel')?.addEventListener('click', close);

  // Save button — persist selected preset and video format to zoneStore
  overlay.querySelector('.ps-save')?.addEventListener('click', () => {
    const w = parseInt(overlay!.querySelector<HTMLInputElement>('.ps-width')!.value, 10) || 1920;
    const h = parseInt(overlay!.querySelector<HTMLInputElement>('.ps-height')!.value, 10) || 1080;
    const fps = parseFloat(overlay!.querySelector<HTMLInputElement>('.ps-fps')!.value) || 30;
    const interlaced = overlay!.querySelector<HTMLInputElement>('.ps-interlaced')!.checked;
    zoneStore.getState().setVideoFormat({ width: w, height: h, fps, interlaced });
    zoneStore.getState().setActivePreset(currentPresetIndex);
    close();
  });

  // Escape key
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  // Video preset dropdown changes width/height/fps
  const videoPreset = overlay.querySelector<HTMLSelectElement>('.ps-video-preset');
  const widthInput = overlay.querySelector<HTMLInputElement>('.ps-width');
  const heightInput = overlay.querySelector<HTMLInputElement>('.ps-height');
  const fpsInput = overlay.querySelector<HTMLInputElement>('.ps-fps');

  videoPreset?.addEventListener('change', () => {
    const val = videoPreset.value;
    const match = val.match(/(\d+)x(\d+)\s*.*?(\d+)\s*fps/);
    if (match && widthInput && heightInput && fpsInput) {
      widthInput.value = match[1];
      heightInput.value = match[2];
      fpsInput.value = match[3];
    }
  });

  // Layout preset dropdown changes zone diagram
  const layoutSelect = overlay.querySelector<HTMLSelectElement>('.ps-layout-select');
  const diagramEl = overlay.querySelector('#ps-zone-diagram');

  layoutSelect?.addEventListener('change', () => {
    currentPresetIndex = parseInt(layoutSelect.value, 10);
    if (diagramEl) {
      diagramEl.innerHTML = renderZoneDiagram(ZONE_PRESETS[currentPresetIndex]);
    }
  });
}

function close(): void {
  if (overlay) {
    overlay.remove();
    overlay = null;
    eventBus.emit('ui:dialogClosed', { dialogId: 'project-settings' });
  }
}
